import { useTealium } from '../hooks/use-tealium';

/**
 * Tracking definition
 *
 * Every event tracking uses one of these definitions.
 * We use this document to keep track in general, similar to a documentary.
 *
 * @revisit
 * - Fyi, the old tracking concept is inconsistent and temporary at this point
 * - v2 means the new tracking concept which is coming in 10/2022
 *
 * Usage:
 *
 * ```ts
 * import { trackByKey } from '@tuic/lib/tracking/tracking';
 * trackByKey['my.tracking.key'](3);
 * ```
 */
const { trackEvent } = useTealium();

type trackByKey = Record<string, any>;
type propsObject = Record<string, any>;

const trackByKey: trackByKey = {
  'account.header.logo': () =>
    trackEvent({
      event_action: 'Logo',
      event_category: 'Account-Header-Footer',
      event_label: 'Mein Schiff',

      // GA 4
      event_name: 'click',
      click_label: 'navi-main.logo.Mein Schiff Logo',
    }),

  'account.footer.menu.link': (props?: propsObject) =>
    trackEvent({
      event_action: 'Footer',
      event_category: 'Account-Header-Footer',

      // GA 4
      event_name: 'click',
      ...props,
    }),

  'account.content.button.logout': () =>
    trackEvent({
      event_action: 'Formular',
      event_category: 'Account-Kontoverwaltung',
      event_label: 'Link_Abmelden',

      // GA 4
      event_name: 'click',
      click_label: 'content.button.account-settings.Abmelden',
    }),

  'account.content.button.my-journey': () =>
    trackEvent({
      event_action: 'Formular',
      event_category: 'Account-Kontoverwaltung',
      event_label: 'CTA_Meine Reise öffnen',

      // GA 4
      event_name: 'click',
      click_label: 'content.button.account-settings.Meine Reise öffnen',
    }),

  'account.content.button.bookmark-list': () =>
    trackEvent({
      event_action: 'Formular',
      event_category: 'Account-Kontoverwaltung',
      event_label: 'CTA_Merkliste öffnen',

      // GA 4
      event_name: 'click',
      click_label: 'content.button.account-settings.Merkliste öffnen',
    }),

  'account.content.button.preferences': (region: string, isChecked: boolean) =>
    trackEvent({
      event_action: 'Favoriten',
      event_category: 'Account-Kontoverwaltung',
      event_label: `Regionsauswahl_${region}-${Number(isChecked)}`,

      // GA 4
      event_name: 'selection-favorite-region',
      selection: `${region}-${Number(isChecked)}`,
    }),

  'account.content.form.sign-up': () =>
    trackEvent({
      event_action: 'Formular',
      event_category: 'Account-Login',
      event_label: 'CTA_Jetzt registrieren',

      // GA 4
      event_name: 'click',
      click_label: 'content.button.account-login.Jetzt registrieren',
    }),

  'account.content.form.sign-in': () =>
    trackEvent({
      event_action: 'Formular',
      event_category: 'Account-Login',
      event_label: 'CTA_Anmelden',

      // GA 4
      event_name: 'click',
      click_label: 'content.button.account-login.Anmelden',
    }),

  'account.content.form.sign-in-success': () =>
    trackEvent({
      event_action: 'Formular',
      event_category: 'Account-Login',
      event_label: 'Anmelden_success',

      // GA4
      event_name: 'login',
      login_variant: 'global',
      login_method: 'password',
    }),

  'account.content.form.forgot-password': () =>
    trackEvent({
      event_action: 'Formular',
      event_category: 'Account-Login',
      event_label: 'Link_Passwort vegessen',

      // GA 4
      event_name: 'click',
      click_label: 'content.link.account-login.Passwort vegessen',
    }),

  'account.content.button.success-back-home': () =>
    trackEvent({
      event_action: 'Bestätigung',
      event_category: 'Account-Registrierung',
      event_label: 'CTA_Zur Startseite',

      // GA 4
      event_name: 'click',
      click_label: 'content.button.account-sign-up.Zur Startseite',
    }),

  'account.content.form.sign-up-complete': () =>
    trackEvent({
      event_action: 'Formular',
      event_category: 'Account-Registrierung',
      event_label: 'CTA_Registrierung abschließen',

      // GA 4
      event_name: 'click',
      click_label: 'content.button.account-sign-up.Registrierung abschließen',
    }),

  'account.content.form.checkbox-newsletter': (isChecked: boolean) =>
    trackEvent({
      event_action: 'Formular',
      event_category: 'Account-Registrierung',
      event_label: `${isChecked ? 'Yes' : 'No'}_Newsletter`,

      // GA 4
      event_name: 'form',
      form_name: 'account.account-sign-up.Newsletter',
      form_interaction: `click.newsletter-${Number(isChecked)}`,
    }),

  'account.content.form.checkbox-tos': (isChecked: boolean) =>
    trackEvent({
      event_action: 'Formular',
      event_category: 'Account-Registrierung',
      event_label: `${
        isChecked ? 'Yes' : 'No'
      }_Datenschutzhinweise und Nutzungsbedingungen`,

      // GA 4
      event_name: 'click',
      form_name:
        'account.account-sign-up.Datenschutzhinweise und Nutzungsbedingungen',
      form_interaction: `click.tos-${Number(isChecked)}`,
    }),

  'account.content.form.privacy-policy-newsletter': () =>
    trackEvent({
      event_action: 'Formular',
      event_category: 'Account-Registrierung',
      event_label: 'Link_Datenschutzhinweise Newsletter',

      // GA 4
      event_name: 'click',
      click_label:
        'content.link.account-sign-up.Datenschutzhinweise Newsletter',
    }),

  'account.content.form.privacy-policy': () =>
    trackEvent({
      event_action: 'Formular',
      event_category: 'Account-Registrierung',
      event_label: 'Link_Datenschutzhinweise',

      // GA 4
      event_name: 'click',
      click_label: 'content.link.account-sign-up.Datenschutzhinweise',
    }),

  'account.content.form.tos': () =>
    trackEvent({
      event_action: 'Formular',
      event_category: 'Account-Registrierung',
      event_label: 'Link_Nutzungsbedingungen',

      // GA 4
      event_name: 'click',
      click_label: 'content.link.account-sign-up.Nutzungsbedingungen',
    }),

  'account.modal.button.delete': () =>
    trackEvent({
      event_action: 'Overlay-Konto-löschen',
      event_category: 'Account-Kontoverwaltung',
      event_label: 'CTA_Konto endgültig löschen',

      // GA 4
      event_name: 'click',
      click_label: 'layer.button.account-settings.Konto endgültig löschen',
    }),

  'account.modal.button.new-email': () =>
    trackEvent({
      event_action: 'Overlay',
      event_category: 'Account-Fehlende-Mailbestätigung',
      event_label: 'CTA_Neue Bestätigungsmail',

      // GA 4
      event_name: 'click',
      click_label: 'layer.button.account-confirmation.Neue Bestätigungsmail',
    }),

  'bookmark-list.content.button.remove-trip': () =>
    trackEvent({
      event_action: 'Icon_Reise entfernen',
      event_category: 'Merkliste-LoggedIn',
      event_label: 'Link_Reise entfernen',

      // GA 4
      event_name: 'click',
      click_label: 'content.button.bookmark-list.Reise entfernen',
    }),

  'bookmark-list.content.button.trip-details': (props: propsObject) =>
    trackEvent({
      event_action: 'Link_Details zur Reise',
      event_category: 'Merkliste-LoggedIn',
      event_label: `tripCode_${props.tripCode}`,

      // GA 4
      event_name: 'click',
      click_label: 'content.button.bookmark-list.Details zur Reise',
    }),

  'bookmark-list.content.button.book-trip': (props: propsObject) =>
    trackEvent({
      event_action: 'CTA_Jetzt buchen',
      event_category: 'Merkliste-LoggedIn',
      event_label: `tripCode_${props.tripCode}`,

      // GA 4
      event_name: 'click',
      click_label: `content.button.bookmark-list-${props.tripCode}.Jetzt buchen`, // @revisit
    }),

  'bookmark-list.content.button.edit-trip': (props: propsObject) =>
    trackEvent({
      event_action: 'CTA_Planung bearbeiten',
      event_category: 'Merkliste-LoggedIn',
      event_label: `tripCode_${props.tripCode}`,

      // GA 4
      event_name: 'click',
      click_label: `content.button.bookmark-list-${props.tripCode}.Planung bearbeiten`, // @revisit
    }),

  'main.header.logo': () =>
    trackEvent({
      event_action: 'Header',
      event_category: 'Navigation',
      event_label: 'Icon_Logo',

      // GA 4
      event_name: 'click',
      click_label: 'navi-main.logo.Mein Schiff Logo',
    }),

  'main.header.meta.button.contact': () =>
    trackEvent({
      event_action: 'Header',
      event_category: 'Navigation',
      event_label: 'Kontakt',

      // GA 4
      event_name: 'click',
      click_label: 'navi.link.Kontakt',
    }),

  'main.header.meta.button.login': () =>
    trackEvent({
      event_action: 'Header',
      event_category: 'Navigation',
      event_label: 'Anmelden',

      // GA 4
      event_name: 'click',
      click_label: 'navi.link.Anmelden',
    }),

  'main.header.meta.button.inclusive': () =>
    trackEvent({
      event_action: 'Header',
      event_category: 'Navigation',
      event_label: 'Inklusivleistungen',

      // GA 4
      event_name: 'click',
      click_label: 'navi.link.Inklusivleistungen',
    }),

  'main.header.meta.button.sale': () =>
    trackEvent({
      event_action: 'Header',
      event_category: 'Navigation',
      event_label: 'Unsere Angebote',

      // GA 4
      event_name: 'click',
      click_label: 'navi.link.Unsere Angebote',
    }),

  'main.header.menu.link': (props?: propsObject) =>
    trackEvent({
      event_action: 'Navigationsebene',
      event_category: 'Navigation',

      // GA 4
      event_name: 'click',
      ...props,
    }),

  'main.footer.logo': () =>
    trackEvent({
      event_action: 'Footer',
      event_category: 'Navigation',
      event_label: 'Icon_Logo',

      // GA 4
      event_name: 'click',
      click_label: 'footer.logo.Mein Schiff Logo',
    }),

  'main.footer.menu.link': (props?: propsObject) =>
    trackEvent({
      event_action: 'Footer',
      event_category: 'Navigation',

      // GA 4
      event_name: 'click',
      ...props,
    }),

  'main.footer.social-media.icon': (props?: propsObject) =>
    trackEvent({
      event_action: 'Footer',
      event_category: 'Navigation',

      // GA 4
      event_name: 'click',
      ...props,
    }),

  'travel-planning.content.button.delete-trip': () =>
    trackEvent({
      event_action: 'Reiseinfos',
      event_category: 'Reiseplanung',
      event_label: 'Link_Plan löschen',

      // GA 4
      event_name: 'click',
      click_label: 'content.link.reiseplanung.Plan löschen',
    }),

  'travel-planning.content.button.print-trip': () =>
    trackEvent({
      event_action: 'Reiseinfos',
      event_category: 'Reiseplanung',
      event_label: 'Link_Plan drucken',

      // GA 4
      event_name: 'click',
      click_label: 'content.button.reiseplanung.Plan drucken',
    }),

  'travel-planning.content.button.trip-itinerary': () =>
    trackEvent({
      event_action: 'Reiseinfos',
      event_category: 'Reiseplanung',
      event_label: 'CTA_Reiseplan_Anzeigen',

      // GA 4
      event_name: 'click',
      click_label: 'content.button.reiseplanung.Reiseplan_Anzeigen',
    }),

  'travel-planning.content.form.dates': (value: string) =>
    trackEvent({
      event_action: 'Reiseinfos',
      event_category: 'Reiseplanung',
      event_label: `Dropdown_Reisetermine-${value}`,

      // GA 4
      event_name: 'form',
      form_name: 'reiseplanung.dates',
      form_interaction: `click.Reisetermine ${value}`,
    }),

  'travel-planning.content.form.airport': (label: string, airport?: string) =>
    trackEvent({
      event_action: 'Flüge',
      event_category: 'Reiseplanung',
      event_label: `Dropdown_${label}${airport === '' ? '' : `-${airport}`}`,

      // GA 4
      event_name: 'form',
      form_name: 'reiseplanung.airport',
      form_interaction: `click.${label}${airport === '' ? '' : `-${airport}`}`,
    }),

  'travel-planning.content.form.pax-adults': (pax: number) =>
    trackEvent({
      event_action: 'Mitreisende_Erwachsene',
      event_category: 'Reiseplanung',
      event_label: `${pax} Erwachsene`,

      // GA 4
      event_name: 'form',
      form_name: 'reiseplanung.pax-adults',
      form_interaction: `click.${pax} Erwachsene`,
    }),

  'travel-planning.content.form.pax-children': (pax: number) =>
    trackEvent({
      event_action: 'Mitreisende_Kinder',
      event_category: 'Reiseplanung',
      event_label: `${pax} Kinder`,

      // GA 4
      event_name: 'form',
      form_name: 'reiseplanung.pax-children',
      form_interaction: `click.${pax} Kinder`,
    }),

  'travel-planning.content.form.pregnancy-baby-hint': () =>
    trackEvent({
      event_action: 'Mitreisende_Babyinfos',
      event_category: 'Reiseplanung',
      event_label: `Link_pregnancy-baby-hint`,

      // GA 4
      event_name: 'click',
      click_label:
        'content.link.reiseplanung.Sie sind schwanger oder Reisen mit Baby?',
    }),

  'travel-planning.content.form.cabin': (type: string) =>
    trackEvent({
      event_action: 'Kabinenauswahl',
      event_category: 'Reiseplanung',
      event_label: `CTA_${type}`,

      // GA 4
      event_name: 'click',
      form_name: 'reiseplanung.cabin',
      form_interaction: `click.Kabinenauswahl ${type}`,
    }),

  'travel-planning.content.form.tariff': (
    type: string,
    changed?: boolean,
    expanded?: boolean,
  ) =>
    trackEvent({
      event_action: 'Tarifoptionen',
      event_category: 'Reiseplanung',
      event_label: `${type}-Tarif_${
        changed ? 'Auswählen' : expanded ? 'Einklappen' : 'Ausklappen'
      }`,

      // GA 4
      event_name: 'form',
      form_name: 'reiseplanung.tariff',
      form_interaction: `click.Tarif ${type}`,
    }),

  'travel-planning.content.form.notes': (length: number) =>
    trackEvent({
      event_action: 'Notizen',
      event_category: 'Reiseplanung',
      event_label: `${length ? 'Befüllen' : 'Leeren'}`,

      // GA 4
      event_name: 'form',
      form_name: 'reiseplanung.notes',
      form_interaction: 'filled.Notizen',
    }),

  'travel-planning.content.button.save-trip': () =>
    trackEvent({
      event_action: 'Abschluss',
      event_category: 'Reiseplanung',
      event_label: 'CTA_Reiseplanung speichern',

      // GA 4
      event_name: 'click',
      click_label: 'content.button.reiseplanung.Reiseplanung speichern',
    }),

  'travel-planning.content.button.back-trip': () =>
    trackEvent({
      event_action: 'Abschluss',
      event_category: 'Reiseplanung',
      event_label: 'CTA_Zurück',

      // GA 4
      event_name: 'click',
      click_label: 'content.button.reiseplanung.Zurück',
    }),

  'travel-planning.content.button.book-trip': () =>
    trackEvent({
      event_action: 'Abschluss',
      event_category: 'Reiseplanung',
      event_label: 'CTA_Jetzt buchen',

      // GA 4
      event_name: 'click',
      click_label: 'content.button.reiseplanung.Jetzt buchen',
    }),

  'travel-planning.content.scroll-depth': (percentage: number) =>
    trackEvent({
      event_action: 'Planungstool',
      event_category: 'Scrollen',
      event_label: `${percentage}%`,

      // GA 4
      event_name: 'scroll-depth',
      scroll_depth: `${percentage}`,
    }),
  'travel-credit.review.payout': () =>
    trackEvent({
      event_name: 'journey_credit',
      journey_credit: 'review-payout',
    }),
  'travel-credit.review.apply': () =>
    trackEvent({
      event_name: 'journey_credit',
      journey_credit: 'review-transfer',
    }),
  'travel-credit.success.payout': () =>
    trackEvent({
      event_name: 'journey_credit',
      journey_credit: 'success-payout',
    }),
  'travel-credit.success.apply': () =>
    trackEvent({
      event_name: 'journey_credit',
      journey_credit: 'success-transfer',
    }),
  'travel-credit.error': () =>
    trackEvent({
      event_name: 'journey_credit',
      journey_credit: 'error',
    }),
};

export { trackByKey };
